<template>
    <div
        ref="Reader"
        class="Reader absolute h-full w-full overflow-hidden bg-theme-bg-reader"
        dusk="Reader"
        :data-theme="ui.themeSelected"
        :style="{
            '--ReaderHeaderHeight': ui.headerbar.offsetHeight + 'px',
            '--ReaderToolbarHeight': ui.toolbar.offsetHeight + 'px',
            '--sidebar-offset-left': sidebarContentOffset.left + 'px',
            '--sidebar-offset-right': sidebarContentOffset.right + 'px',
            '--sidebar-panel-width': '380px',
        }"
    >
        <SkipLinks />
        <ReaderHeader />
        <LoadingSpinner
            v-show="isLoading || ui.pagination.current !== currentPage"
            :position="loadingSpinnerPosition"
        />

        <ReaderNotebook v-if="isNotAudiobook" />

        <template v-if="isAudiobook">
            <AudiobookAbout />
        </template>

        <main id="main-content" role="presentation">
            <component
                :is="readerViewer"
                v-if="isLoadingProcessFinished && polpoSessionInitiated && readerViewer"
                :key="reader.layout"
            />
        </main>
        <SideControlPaginator v-if="showPaginator" />

        <SmartZoom v-if="isPdf" />
        <ReaderThumbnails v-if="isPdf" />
        <ReaderTableOfContents v-if="isNotAudiobook" />
        <transition name="fade">
            <ReaderSelectionTool />
        </transition>
        <ReaderIntegrationsTool v-if="isNotAudiobook" />
        <SessionCountdown v-if="concurrencyLimit.enforce" />
        <SessionExpiredModal />
        <ComponentToast />
        <ReaderOnboarding v-if="onboarding.hasToBeShown" />
        <ReaderPromoteAppModal v-if="promoteApp.hasToBeShown" />
        <ComponentTts v-if="isNotAudiobook && ui.listening.opened && ui.listening.enabled" />
        <ReaderCreateNotesModal />
        <ComponentBuyOrLoginButton v-if="showBuyOrLoginButton" />
        <SocialShare v-if="isShareEnabled" />
        <AddToLists v-if="addToLists.show" />
        <ReaderSidebarPlugin v-if="canUseReaderPlugins" />
        <ConfirmationModal />

        <ReaderFooter v-if="isNotAudiobook" />
    </div>
</template>

<script>
'use strict';

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { throttle } from 'lodash';
import ReaderHeader from '_shared_/UI/ReaderHeader.vue';
import ReaderFooter from '_shared_/UI/ReaderFooter.vue';
import ReaderThumbnails from '_shared_/UI/Sidebar/ReaderThumbnails.vue';
import ReaderTableOfContents from '_shared_/UI/Sidebar/ReaderTableOfContents.vue';
import ReaderSidebarPlugin from '_shared_/UI/Sidebar/ReaderSidebarPlugin.vue';
import LoadingSpinner from '_shared_/UI/Utilities/LoadingSpinner.vue';
import SmartZoom from '_shared_/UI/SmartZoom/SmartZoom.vue';
import SessionExpiredModal from '_shared_/UI/Sessions/SessionExpiredModal.vue';
import ConfirmationModal from '_shared_/UI/Components/ConfirmationModal.vue';
import SessionCountdown from '_shared_/UI/SessionsConcurrency/SessionCountdown.vue';
import ReaderSelectionTool from '_shared_/UI/ReaderSelectionTool/ReaderSelectionTool.vue';
import ReaderIntegrationsTool from '_shared_/UI/ReaderSelectionTool/ReaderIntegrationsTool.vue';
import ComponentToast from '_shared_/UI/Components/ComponentToast.vue';
import ReaderOnboarding from '_shared_/UI/ReaderOnboarding/ReaderOnboarding.vue';
import ComponentTts from '_shared_/UI/Components/ComponentTts.vue';
import ComponentNetworkStatus from '_shared_/UI/Components/ComponentNetworkStatus.vue';
import ComponentBuyOrLoginButton from '_shared_/UI/Components/ComponentBuyOrLoginButton.vue';
import AudiobookAbout from '../../../_shared_/UI/Audiobooks/AudiobookAbout.vue';
import SocialShare from '_shared_/UI/SocialShare/SocialShare.vue';
import ReaderCreateNotesModal from '_shared_/UI/ReaderSelectionTool/ReaderCreateNotesModal.vue';
import SideControlPaginator from '../../../_shared_/UI/Components/SideControlPaginator.vue';
import ReaderPromoteAppModal from '../../../_shared_/UI/ReaderPromoteApp/ReaderPromoteAppModal.vue';
import handleUrl from '../../../_shared_/js/utils/handleUrl';
import isTouchDevice from '../../../_shared_/js/utils/isTouchDevice';
import AddToLists from '../../../_shared_/UI/Lists/AddToLists.vue';
import ReaderNotebook from '../../../_shared_/UI/Notebook/ReaderNotebook.vue';
import SkipLinks from '../../../_shared_/UI/Components/SkipLinks.vue';
import { fixedPageViewTracker, navigationAPI, trackingEventsListener } from '../communication';

export default {
    el: '#Reader',
    name: 'Reader',
    components: {
        ReaderTableOfContents,
        ReaderHeader,
        ReaderFooter,
        ReaderNotebook,
        ReaderThumbnails,
        ReaderSidebarPlugin,
        LoadingSpinner,
        SmartZoom,
        SessionCountdown,
        SessionExpiredModal,
        ConfirmationModal,
        ReaderSelectionTool,
        ReaderIntegrationsTool,
        ComponentToast,
        ReaderOnboarding,
        ComponentTts,
        ComponentNetworkStatus,
        ComponentBuyOrLoginButton,
        AudiobookAbout,
        SocialShare,
        ReaderCreateNotesModal,
        ReaderPromoteAppModal,
        AddToLists,
        SideControlPaginator,
        SkipLinks,
    },
    computed: {
        ...mapState([
            'concurrencyLimit',
            'isLoadingProcessFinished',
            'polpoSessionInitiated',
            'fileType',
            'publication',
            'reader',
            'isFixedLayout',
            'isLoading',
            'loadingSpinnerPosition',
            'ui',
            'uiHidden',
            'coniglio',
            'volpeHost',
            'onboarding',
            'lastLocation',
            'promoteApp',
            'addToLists',
            'issue',
        ]),
        ...mapGetters([
            'isEpub',
            'isPdf',
            'isAudiobook',
            'currentPage',
            'hasFeatureActive',
            'showBuyOrLoginButton',
            'onlyDesktopVisible',
            'scrollPageMode',
            'isZoomActive',
            'isShareEnabled',
            'canUseReaderPlugins',
            'sidebarContentOffset',
        ]),
        readerViewer() {
            if (this.isPdf) {
                return this.reader.layout === 'scroll'
                    ? () => import('./partials/Pdf/PdfScrollReaderViewer.vue')
                    : () => import('./partials/Pdf/PdfPagedReaderViewer.vue');
            }
            if (this.isEpub) {
                return () => import('./partials/Epub/EpubViewer.vue');
            }
            if (this.isAudiobook) {
                return () => import('./partials/Audiobook/AudiobookViewer.vue');
            }
            return undefined;
        },
        isNotAudiobook() {
            return this.isPdf || this.isEpub;
        },
        showPaginator() {
            // Determines whether to display the paginator.
            // Hides the paginator if in scroll mode, audiobook mode, or on touch devices in mobile view.
            // On non-touch devices, always show paginator (needed for browser zoom accessibility).

            const isScrollModeOrAudiobook = this.scrollPageMode || this.isAudiobook;
            const isMobileViewOnTouchDevice = !this.onlyDesktopVisible && isTouchDevice;
            const isZoomAndUiVisible = this.isZoomActive && !this.uiHidden;

            if (isScrollModeOrAudiobook || isMobileViewOnTouchDevice) {
                return false;
            }

            return !this.isZoomActive || isZoomAndUiVisible;
        },
    },
    watch: {
        isFixedLayout(isFixed) {
            if (isFixed) {
                fixedPageViewTracker.init(
                    this.$bus,
                    () => this.$store.getters.currentPages,
                    this.$store.state.reader.pageViewConfirmDelayMs
                );
            }
        },
        // The store's default fileType is 'pdf', so isPdf is true at mount
        // before LoadingProcess sets the real fileType. Wait for the loading
        // flag to flip before inferring the content type for the tracker.
        isLoadingProcessFinished(finished) {
            if (finished && this.isPdf) {
                fixedPageViewTracker.init(
                    this.$bus,
                    () => this.$store.getters.currentPages,
                    this.$store.state.reader.pageViewConfirmDelayMs
                );
            }
        },
    },
    mounted() {
        this.detectScreenSize();
        window.addEventListener('resize', throttle(this.detectScreenSize, 100), true);
        this.watchNetworkStatus();
        document.addEventListener('keydown', this.handleKeyPressed, false);
        document.addEventListener('click', event => handleUrl(event, url => navigationAPI.openLink({ url })), true);
        trackingEventsListener.init(this.$bus);

        // Defensive: handle the case where the loading process already
        // finished before this component mounted (e.g. on remounts).
        if (this.isLoadingProcessFinished && this.isPdf) {
            fixedPageViewTracker.init(
                this.$bus,
                () => this.$store.getters.currentPages,
                this.$store.state.reader.pageViewConfirmDelayMs
            );
        }
    },
    beforeDestroy() {
        fixedPageViewTracker.destroy();
        window.removeEventListener('resize', this.detectScreenSize);
        document.removeEventListener('keydown', this.handleKeyPressed);
    },
    methods: {
        ...mapMutations([
            'setDeviceDetails',
            'setTotalPages',
            'setIssueId',
            'setNetworkStatus',
            'closeAllSettingsOrFeatures',
        ]),
        ...mapActions(['goToPrevPage', 'goToNextPage', 'openSearch', 'notifyLocationChange', 'toggleUi']),
        detectScreenSize() {
            this.setDeviceDetails({
                width: this.$refs['Reader'].clientWidth,
                height: this.$refs['Reader'].clientHeight,
                orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait',
            });
        },
        handleKeyPressed(e) {
            if (this.isAudiobook) return;

            if (this.reader.keyboardNavigationEnabled) {
                if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    this.goToPrevPage();
                }
                if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    this.goToNextPage();
                }
            }
            if (this.hasFeatureActive && e.key === 'Escape') {
                (e.preventDefault(), this.closeAllSettingsOrFeatures());
            }
            if (this.ui.search.enabled && (e.metaKey || e.ctrlKey) && (e.key === 'f' || e.key === 'F')) {
                (e.preventDefault(), this.toggleUi({ uiHidden: false }), this.openSearch());
            }
        },
        watchNetworkStatus() {
            this.setNetworkStatus({
                isOffline: !navigator.onLine,
            });
            window.addEventListener('online', () => {
                window.app = window.app || {};
                window.app.network = 'online';
                this.setNetworkStatus({
                    isOffline: false,
                });
            });
            window.addEventListener('offline', () => {
                window.app = window.app || {};
                window.app.network = 'offline';
                this.setNetworkStatus({
                    isOffline: true,
                });
            });
        },
    },
};
</script>

<style>
#main-content {
    width: 100%;
    height: 100%;
}

@media screen and (min-width: 571px) {
    .PdfScrollReaderViewer,
    .PdfPagedReaderViewer,
    .AudiobookViewer {
        padding-left: var(--sidebar-offset-left, 0px);
        padding-right: var(--sidebar-offset-right, 0px);
        transition: padding 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    }
}
</style>
